import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import ChatIcon from "@mui/icons-material/Chat";
import CircularProgress from "@mui/material/CircularProgress";
import MDButton from "components/MDButton";
import { useAuth } from "../../context";
import config from "../../config/config";

class WidgetErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("GHLChatWidget crashed:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return null;
    }

    return this.props.children;
  }
}

WidgetErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};

const GHLChatWidgetContent = () => {
  const { verifiedCustomer } = useAuth();
  const [isWidgetReady, setIsWidgetReady] = useState(false);
  const [isChatActive, setIsChatActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const activeSessionRef = useRef(null);

  const env = process.env.REACT_APP_ENV?.toUpperCase() || "LOCAL";
  const appConfig = config[env];
  const { GHL_WIDGET_ID: widgetId, GHL_LOCATION_ID: locationId } = appConfig || {};

  const user = verifiedCustomer
    ? {
        id: verifiedCustomer.id,
        userEmail: verifiedCustomer.userEmail,
        firstName: verifiedCustomer.firstName,
        lastName: verifiedCustomer.lastName,
      }
    : null;

  const shouldShow = widgetId && locationId && user?.id;

  useEffect(() => {
    if (!user?.id) {
      setIsChatActive(false);
      setIsWidgetReady(false);
      setIsLoading(false);
      cleanupWidget();
    }
  }, [user?.id]);

  useEffect(() => {
    if (!isChatActive) return;

    let isMounted = true;
    const currentSessionId = `${widgetId}-${user?.id}`;
    activeSessionRef.current = currentSessionId;

    if (!user?.id || !widgetId || !locationId) {
      if (!user?.id) cleanupWidget();
      return;
    }

    const initializeSecureSession = async () => {
      setIsLoading(true);
      try {
        const ghlStorageKey = `${locationId}_${widgetId}_lead-connecter-live-widget-message-data`;
        const internalSessionKey = `ghl_widget_active_user_id_${widgetId}`;

        const storedGHLData = localStorage.getItem(ghlStorageKey);
        const activeUserId = localStorage.getItem(internalSessionKey);

        if (storedGHLData && activeUserId === user.id) {
          try {
            const parsedData = JSON.parse(storedGHLData);
            if (parsedData.conversationId && parsedData.visitorId && parsedData.token) {
              if (parsedData.socketConnected !== true) {
                parsedData.socketConnected = true;
                localStorage.setItem(ghlStorageKey, JSON.stringify(parsedData));
              }
              if (isMounted && activeSessionRef.current === currentSessionId) {
                setIsWidgetReady(true);
              }
              return;
            }
          } catch (e) {
            console.warn("Failed to parse stored data, creating new session", e);
          }
        }

        const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();

        const rawPayload = JSON.stringify({
          name: fullName,
          email: user.userEmail || "",
          locationId: locationId,
          chatWidgetId: widgetId,
          chatType: "Live_Chat",
          enableContactForm: true,
        });

        const response = await fetch("https://services.leadconnectorhq.com/chat-widget/forms", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: rawPayload,
        });

        if (!response.ok) throw new Error(`API Error: ${response.status}`);

        const result = await response.json();

        const authData = {
          conversationId: result.conversationId,
          visitorId: result.visitorId,
          token: result.token,
          locationId: locationId,
          widgetId: widgetId,
          socketConnected: true,
        };

        if (isMounted && activeSessionRef.current === currentSessionId) {
          localStorage.setItem(ghlStorageKey, JSON.stringify(authData));
          localStorage.setItem(internalSessionKey, user.id);
          setIsWidgetReady(true);
        }
      } catch (error) {
        console.error("❌ GHL Auth Error:", error);
        if (isMounted && activeSessionRef.current === currentSessionId) {
          toast.error(
            "Unable to connect to chat. Please create a support ticket and our team will get back to you.",
            {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            }
          );
        }
      } finally {
        if (isMounted && activeSessionRef.current === currentSessionId) {
          setIsLoading(false);
        }
      }
    };

    initializeSecureSession();

    return () => {
      isMounted = false;
      cleanupWidget();
    };
  }, [isChatActive, user?.id, widgetId, locationId]);

  useEffect(() => {
    if (!shouldShow) return;
    if (!isWidgetReady) return;

    const openWidget = () => {
      try {
        if (window.leadConnector?.chatWidget) {
          window.leadConnector.chatWidget.openWidget();
        }
      } catch (e) {
        console.error("❌ GHL: Failed to open widget");
      }
    };

    if (document.getElementById("ghl-chat-script")) {
      window.addEventListener("LC_chatWidgetLoaded", openWidget, {
        once: true,
      });
      return () => {
        window.removeEventListener("LC_chatWidgetLoaded", openWidget);
      };
    }

    const script = document.createElement("script");
    script.id = "ghl-chat-script";
    script.src = "https://widgets.leadconnectorhq.com/loader.js";
    script.async = true;
    script.defer = true;
    script.setAttribute(
      "data-resources-url",
      "https://widgets.leadconnectorhq.com/chat-widget/loader.js"
    );

    script.setAttribute("data-widget-id", widgetId);

    const handleWidgetLoaded = () => {
      openWidget();
    };

    window.addEventListener("LC_chatWidgetLoaded", handleWidgetLoaded, {
      once: true,
    });
    document.body.appendChild(script);

    return () => {
      window.removeEventListener("LC_chatWidgetLoaded", handleWidgetLoaded);
      const existingScript = document.getElementById("ghl-chat-script");
      if (existingScript) existingScript.remove();
    };
  }, [isWidgetReady, widgetId, locationId, shouldShow]);

  const cleanupWidget = () => {
    const script = document.getElementById("ghl-chat-script");
    if (script) script.remove();

    const iframes = document.querySelectorAll("iframe[id^='chat-widget-']");
    iframes.forEach((frame) => frame.remove());
  };

  if (!shouldShow) return null;
  if (!user?.id) return null;

  if (isWidgetReady) return null;

  return (
    <MDButton
      variant="gradient"
      color="success"
      onClick={() => setIsChatActive(true)}
      disabled={isLoading}
      startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <ChatIcon />}
      sx={{
        position: "fixed",
        bottom: "30px",
        right: "30px",
        zIndex: 9999,
        borderRadius: "50px",
        padding: "12px 24px",
        fontWeight: "600",
        textTransform: "none",
        boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
        opacity: isLoading ? 0.6 : 1,
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        },
      }}
      aria-label="Open Support Chat"
    >
      {isLoading ? "Connecting..." : "Chat with us"}
    </MDButton>
  );
};

const GHLChatWidget = () => {
  return (
    <WidgetErrorBoundary>
      <GHLChatWidgetContent />
    </WidgetErrorBoundary>
  );
};

export default GHLChatWidget;
